import { useDeletePlayMutation } from '@carboncopyco/data';
import {
  ArchiveIcon,
  DeleteIcon,
  MoreVerticalOutlineIcon,
  PlayIcon,
  ShowOutlineIcon,
} from '@carboncopyco/icons';
import type { Play } from '@carboncopyco/types';
import {
  Button,
  ColorHex,
  ConfirmAlert,
  Dropdown,
  DropdownItem,
  Loading,
  TextTooltip,
  Tooltip,
  TooltipBubble,
  notify,
} from '@carboncopyco/ui';
import { isDesktop, usePlaybookAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useRef } from 'react';
import {
  PlaybookGiveADemoTooltip,
  PlaybookPreviewTooltip,
} from '../../../Tooltip';
import { usePlaybookPageProps } from '../PlaybookPageContext';
import { usePresentationsContext } from '../PresentationsContext';

interface PresentationRowButtonsProps {
  presentation: Play;
}

export const RowButtons = ({ presentation }: PresentationRowButtonsProps) => {
  const {
    goToPlaybookPreview,
    playPresentation,
    organizationId,
    desktopDomain,
  } = usePlaybookPageProps();

  const { removeItem: removePresentation } = usePresentationsContext();
  const [archivePresentation] = useDeletePlayMutation();
  const [
    showArchiveModal,
    { setTrue: setTruesShowArchiveModal, setFalse: setFalseShowArchiveModal },
  ] = useBoolean();
  const {
    trackPlaybookClickPreview,
    trackPlaybookClickPresent,
    trackPlaybookClickArchivePresentation,
  } = usePlaybookAnalytics('presentations_tab');
  const previewRef = useRef<HTMLDivElement>(null);
  const giveADemoRef = useRef<HTMLButtonElement>(null);
  const [isPresenting, setIsPresenting] = useBoolean(false);
  const [isPresentingPreview, setIsPresentingPreview] = useBoolean(false);
  const isPlaybookLoading = (preview = false) =>
    isDesktop() && (preview ? isPresentingPreview : isPresenting);

  const handlePlay = async () => {
    try {
      if (isDesktop()) {
        setIsPresenting.setTrue();
      }
      if (playPresentation) {
        trackPlaybookClickPresent({
          playbookId: presentation.asset.assetId,
        });
        await playPresentation(presentation);
      }
    } finally {
      setIsPresenting.setFalse();
    }
  };

  const handlePreview = async () => {
    try {
      if (isDesktop()) {
        setIsPresentingPreview.setTrue();
      }
      if (goToPlaybookPreview) {
        trackPlaybookClickPreview({
          playbookId: presentation.asset.assetId,
        });
        await goToPlaybookPreview(presentation);
      }
    } finally {
      setIsPresentingPreview.setFalse();
    }
  };

  return (
    <div className="flex items-center gap-3">
      <div ref={previewRef}>
        <Button
          dark
          disabled={!isDesktop() || isPlaybookLoading(true)}
          icon={
            isPlaybookLoading(true) ? (
              <Loading />
            ) : (
              <ShowOutlineIcon
                color={!isDesktop() ? ColorHex.natural200 : ColorHex.natural600}
              />
            )
          }
          onClick={handlePreview}
        />
      </div>

      {isDesktop() ? (
        <Tooltip
          background="dark"
          anchorRef={previewRef}
          placement="top"
          noArrow
        >
          <div className="px-1 pb-1 pt-0 leading-none break-words">
            <TextTooltip
              label="Preview"
              color="text-ds-natural-50"
            />
          </div>
        </Tooltip>
      ) : (
        <PlaybookPreviewTooltip
          desktopDomain={desktopDomain}
          previewRef={previewRef}
          location={location}
          playId={presentation.id}
        />
      )}
      <Button
        ref={giveADemoRef}
        dark
        disabled={isPlaybookLoading()}
        icon={
          isPlaybookLoading() ? (
            <Loading />
          ) : (
            <PlayIcon color={ColorHex.natural600} />
          )
        }
        onClick={handlePlay}
        aria-label="Present button"
      />

      {isDesktop() ? (
        <Tooltip
          background="dark"
          anchorRef={giveADemoRef}
          placement="top"
          noArrow
        >
          <div className="px-1 pb-1 pt-0 leading-none break-words">
            <TextTooltip
              label="Present now"
              color="text-ds-natural-50"
            />
          </div>
        </Tooltip>
      ) : (
        <PlaybookGiveADemoTooltip
          desktopDomain={desktopDomain}
          giveADemoRef={giveADemoRef}
          location={location}
          playId={presentation.id}
        />
      )}
      <Dropdown
        asChild
        side="left"
        triggerElement={
          <div>
            <TooltipBubble
              placement="top"
              title="More actions"
            >
              <Button
                aria-label="actions"
                dark
                icon={<MoreVerticalOutlineIcon color={ColorHex.natural600} />}
              />
            </TooltipBubble>
          </div>
        }
      >
        <DropdownItem
          iconLeft={<ArchiveIcon size={18} />}
          label="Archive"
          onClick={() => {
            trackPlaybookClickArchivePresentation({
              playbookId: presentation.id,
            });
            setTruesShowArchiveModal();
          }}
        />
      </Dropdown>
      {showArchiveModal && (
        <ConfirmAlert
          title="Archive presentation"
          description="Archiving will remove this presentation from your environment"
          containerClass="w-ds-normal-modal"
          danger
          yesIcon={<DeleteIcon />}
          yesLabel="Archive"
          show={showArchiveModal}
          onClose={() => {
            setFalseShowArchiveModal();
          }}
          onConfirm={() => {
            archivePresentation({
              organizationId,
              playId: presentation.id,
            })
              .unwrap()
              .then(() => {
                removePresentation(String(presentation.id));
                notify.success('Archived presentation successfully');
              })
              .catch(() =>
                notify.error('There was an error archiving this presentation')
              )
              .finally(setFalseShowArchiveModal);
          }}
        />
      )}
    </div>
  );
};
